import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";

import {
  bookingSearchParamsToQueryString,
  bookingSearchParamsToVehiclePayload,
  type BookingSearchParams,
} from "@/lib/booking/search-params";
import type { RootState } from "@/lib/redux/store";
import { vehiclesService } from "@/lib/services/vehicles-service";
import type { Vehicle } from "@/lib/types/vehicle";

type VehiclesStatus = "idle" | "loading" | "succeeded" | "failed";

type VehiclesState = {
  items: Vehicle[];
  status: VehiclesStatus;
  error: string | null;
  searchKey: string | null;
  selectedVehicleId: number | null;
};

const initialState: VehiclesState = {
  items: [],
  status: "idle",
  error: null,
  searchKey: null,
  selectedVehicleId: null,
};

export function bookingSearchParamsToKey(params: BookingSearchParams): string {
  return bookingSearchParamsToQueryString(params);
}

export const fetchVehicles = createAsyncThunk(
  "vehicles/fetch",
  async (params: BookingSearchParams, { rejectWithValue }) => {
    try {
      const payload = bookingSearchParamsToVehiclePayload(params);
      const response = await vehiclesService.fetchVehicles(payload);

      if (!response.success) {
        return rejectWithValue(response.message);
      }

      return {
        items: response.data,
        searchKey: bookingSearchParamsToKey(params),
      };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load vehicles";

      return rejectWithValue(message);
    }
  },
);

const vehiclesSlice = createSlice({
  name: "vehicles",
  initialState,
  reducers: {
    clearVehicles(state) {
      state.items = [];
      state.status = "idle";
      state.error = null;
      state.searchKey = null;
      state.selectedVehicleId = null;
    },
    setSelectedVehicle(state, action: PayloadAction<number | null>) {
      state.selectedVehicleId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVehicles.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
        state.searchKey = bookingSearchParamsToKey(action.meta.arg);
        state.selectedVehicleId = null;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.items = action.payload.items;
        state.searchKey = action.payload.searchKey;
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.status = "failed";
        state.items = [];
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to load vehicles";
      });
  },
});

export const { clearVehicles, setSelectedVehicle } = vehiclesSlice.actions;
export default vehiclesSlice.reducer;

export const selectVehicles = (state: RootState) => state.vehicles.items;
export const selectVehiclesStatus = (state: RootState) => state.vehicles.status;
export const selectVehiclesError = (state: RootState) => state.vehicles.error;
export const selectVehiclesSearchKey = (state: RootState) =>
  state.vehicles.searchKey;
export const selectSelectedVehicleId = (state: RootState) =>
  state.vehicles.selectedVehicleId;
export const selectSelectedVehicle = (state: RootState) => {
  const selectedId = state.vehicles.selectedVehicleId;
  if (selectedId === null) {
    return null;
  }

  return (
    state.vehicles.items.find((vehicle) => vehicle.type_id === selectedId) ??
    null
  );
};
